import React, { useState } from 'react';
import { useLocation, Navigate, useNavigate, Location } from 'react-router';

import { AuthShell } from '@/components/auth/AuthShell';
import { LoadingIndicator } from '@/components/common/LoadingIndicator';
import { Button } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { useTranslationPrefix } from '@/translations/i18n';
import { isMobileDevice } from '@/utils/userAgent';

export interface EmailVerificationSentLocationState {
  email?: string;
}

const EmailVerificationSent: React.FC = () => {
  const t = useTranslationPrefix('email_verification_sent');
  const location = useLocation() as Location<EmailVerificationSentLocationState | undefined>;
  const navigate = useNavigate();
  const { resendRegisterCode, isResendingRegisterCode } = useAuthContext();

  const [error, setError] = useState<string | null>(null);

  // DEV ONLY: preview without navigating from register flow
  const DEV_MOCK = import.meta.env.MODE === 'development' && !location.state?.email;
  const email = DEV_MOCK ? 'preview@hauserhq.com' : location.state?.email;

  if (!email) {
    return <Navigate to="/register" />;
  }

  const onOpenEmailAppClick = () => {
    window.location.href = 'mailto:';
  };

  const onLoginClick = () => {
    navigate('/login');
  };

  const onResendEmailClick = async () => {
    try {
      setError(null);
      await resendRegisterCode(email);
    } catch (error: any) {
      setError(error.message);
    }
  };

  // on a phone the mail app is the next step, so it takes the one lime action; elsewhere it's signing in
  const mobile = isMobileDevice();

  return (
    <AuthShell
      title="Check your email"
      footer={
        isResendingRegisterCode ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            {t('did_not_receive_email')}
            <button type="button" className="hx-auth-link" onClick={onResendEmailClick}>
              {t('resend_email')}
            </button>
          </>
        )
      }>
      <div data-testid="email-verification-sent">
        <h1 className="hx-auth-title">{t('title')}</h1>
        <p className="hx-auth-sub">
          {t('intro.0')}
          <span style={{ fontWeight: 600, color: '#f7f7f6' }}>{email}</span>. {t('intro.1')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mobile && (
            <Button variant="primary" block onClick={onOpenEmailAppClick}>
              {t('open_email')}
            </Button>
          )}
          <Button variant={mobile ? 'secondary' : 'primary'} block onClick={onLoginClick}>
            {t('log_in')}
          </Button>
          {error && <p className="hx-auth-error">{error}</p>}
        </div>
      </div>
    </AuthShell>
  );
};

export default EmailVerificationSent;
